import { NOMBRE_APP } from './marca';

/**
 * Contenido de la página de ayuda (/ayuda). Se edita aquí, sin tocar la página.
 *
 * - `id` de sección y de pregunta sirven de ancla: /ayuda#amedias-invitar abre
 *   esa pregunta. No cambies un id ya enlazado desde la app (búscalo antes).
 * - `respuesta` es una lista de párrafos; los que empiezan por «- » se pintan
 *   como viñetas. Nada de HTML: así no hay nada que escapar.
 * - `capturas`: imágenes de public/ayuda que acompañan la respuesta, con su
 *   versión en modo oscuro (mismo nombre acabado en -oscuro.webp).
 * - `soloConModulo`: la sección solo sale a quien tiene ese módulo activado
 *   (para módulos de uso más particular, como Taller 3D o Clientes).
 * - `accion`: un botón bajo la respuesta que lleva a la app. Solo sale con la
 *   sesión iniciada y, si lleva `modulo`, a quien lo tiene activado.
 */
export interface PreguntaAyuda {
  id: string;
  pregunta: string;
  respuesta: string[];
  capturas?: CapturaAyuda[];
  accion?: AccionAyuda;
}

export interface AccionAyuda {
  texto: string;
  ruta: string;
  modulo?: string;
}

export interface CapturaAyuda {
  /** Nombre en public/ayuda, sin extensión: «asistente-1» → asistente-1.webp y asistente-1-oscuro.webp */
  archivo: string;
  /** Lo que se ve, para quien no ve la imagen y como pie de foto */
  texto: string;
}

export interface SeccionAyuda {
  id: string;
  titulo: string;
  emoji: string;
  intro?: string;
  soloConModulo?: string;
  preguntas: PreguntaAyuda[];
}

export const AYUDA: SeccionAyuda[] = [
  {
    id: 'empezar',
    titulo: 'Primeros pasos',
    emoji: '👋',
    preguntas: [
      {
        id: 'empezar-que-es',
        pregunta: `¿Qué es ${NOMBRE_APP}?`,
        respuesta: [
          'Una app con varias utilidades, cada una en su módulo: Finanzas para tus cuentas, A medias para gastos compartidos con amigos, Préstamos para las cosas que dejas, y alguno más.',
          'Cada persona ve solo los módulos que tiene activados. Al crear la cuenta tienes Finanzas y A medias.',
        ],
      },
      {
        id: 'empezar-pendiente',
        pregunta: '¿Qué módulos tengo y cómo consigo los demás?',
        respuesta: [
          'Toda cuenta nueva empieza con Finanzas y A medias. Los demás (Taller 3D, Clientes, Préstamos…) los activa un administrador.',
          'Si te sale «Tu cuenta no tiene ningún módulo activado», es que te los han quitado: avisa al administrador y, en cuanto te los active, la pantalla cambia sola.',
          'Si te han invitado a un plan de A medias, no hace falta esperar: abre el enlace de la invitación y entrarás directamente.',
        ],
      },
      {
        id: 'empezar-nombre',
        pregunta: '¿Cómo cambio el nombre con el que salgo?',
        respuesta: [
          'En Configuración (el engranaje de arriba) puedes poner tu nombre. Es el que verán los demás cuando crees un plan.',
        ],
      },
      {
        id: 'empezar-acceso',
        pregunta: '¿Puedo entrar con Apple o con Google?',
        respuesta: [
          'Sí. En la pantalla de entrada tienes «Continuar con Apple» y «Continuar con Google». Si no tenías cuenta, se crea en ese momento.',
          'Si ya tenías cuenta con tu email y contraseña, entra como siempre y ve a Configuración → Perfil → Métodos de acceso para vincular Apple o Google. A partir de ahí podrás entrar con cualquiera de ellos y verás los mismos datos.',
          'Si pulsas Apple o Google con el mismo email que ya usabas, la app te lo dirá: entra como lo hacías hasta ahora y quedarán vinculados solos.',
          'Con Apple puedes elegir «Ocultar mi email». Funciona igual; solo que en tu perfil verás una dirección de Apple en lugar de la tuya.',
        ],
      },
      {
        id: 'empezar-app',
        pregunta: '¿Hay app para el iPhone?',
        respuesta: [
          'Sí: es la misma que la web, con tu misma cuenta y tus mismos datos. Además, en Configuración → Perfil tiene dos cosas que la web no puede hacer:',
          '- Bloquear con Face ID: la app te lo pide al abrirla y al volver a ella tras un minuto.',
          '- Notificaciones: te avisa cuando tengas pendiente pagar tu parte en un plan de A medias.',
          'También funciona sin conexión con lo último que viste: lo que apuntes se guarda en cuanto vuelva la red.',
        ],
      },
      {
        id: 'empezar-borrar-cuenta',
        pregunta: '¿Cómo borro mi cuenta?',
        respuesta: [
          'En Configuración → Perfil → «Eliminar mi cuenta». Te pedirá que escribas ELIMINAR y que vuelvas a identificarte.',
          '- Se borra tu cuenta y todo lo tuyo en Finanzas, Taller 3D, Clientes y Préstamos.',
          '- Los planes de A medias en los que solo estás tú se borran enteros.',
          '- En los planes compartidos, los gastos y pagos se quedan porque son las cuentas del grupo. Tú dejas de ser miembro y tu nombre queda como un participante sin cuenta.',
          'No se puede deshacer. Si quieres guardar tus movimientos, expórtalos antes desde Configuración → Datos.',
        ],
      },
    ],
  },
  {
    id: 'amedias',
    titulo: 'A medias: gastos compartidos',
    emoji: '🤝',
    intro: 'Para viajes, cenas o un piso compartido: cada uno apunta lo que paga y la app calcula quién le debe a quién.',
    preguntas: [
      {
        id: 'amedias-crear',
        pregunta: '¿Cómo creo un plan?',
        respuesta: [
          'Entra en A medias y pulsa «Nuevo plan». Ponle un nombre (por ejemplo, «Asturias agosto»), un icono y, si quieres, las fechas.',
          'Apunta a la gente que participa. Basta con el nombre: no hace falta que usen la app. Tú ya estás incluido.',
        ],
      },
      {
        id: 'amedias-gasto',
        pregunta: '¿Cómo apunto un gasto?',
        respuesta: [
          'Dentro del plan, pulsa «Gasto». Escribe qué es, cuánto costó, quién lo pagó y entre quién se reparte. Hay tres formas de repartir:',
          '- A partes iguales: marcas quién entra y se divide a partes iguales.',
          '- Por partes: cada uno paga un número de partes; con 2 paga el doble que alguien con 1.',
          '- Importes exactos: escribes lo de cada uno; tiene que sumar el total.',
          'Siempre cuadra al céntimo: si sobra uno, se lo queda quien pagó.',
        ],
      },
      {
        id: 'amedias-ticket',
        pregunta: '¿Puedo guardar la foto del ticket?',
        respuesta: [
          'Sí. Al apuntar o editar un gasto, pulsa «Hacer o subir foto». En el móvil puedes hacerla con la cámara en ese momento.',
          'La foto se guarda reducida y la pueden ver todos los del plan con el clip 📎 que aparece junto al gasto.',
        ],
      },
      {
        id: 'amedias-saldos',
        pregunta: '¿Cómo sé quién debe a quién?',
        respuesta: [
          'En la pestaña «Saldos». Arriba sale la lista más corta de pagos para quedar todos en paz; debajo, el saldo de cada uno.',
          'Saldo positivo: le deben dinero. Negativo: debe. Tu saldo también sale en la tarjeta del plan.',
          'Los pagos que propone no siempre coinciden con quién pagó a quién: es la forma de hacer menos transferencias.',
        ],
      },
      {
        id: 'amedias-pago',
        pregunta: 'Ya le he pagado a alguien, ¿cómo lo apunto?',
        respuesta: [
          'Pulsa «Saldar» en la línea de «Quién debe a quién»: el pago sale ya rellenado. También puedes pulsar «Pago» y escribirlo tú.',
          'Los pagos reducen la deuda; cuando todo el mundo está al día, lo verás en verde.',
        ],
      },
      {
        id: 'amedias-invitar',
        pregunta: '¿Cómo invito a los demás a que vean el plan?',
        respuesta: [
          'Si organizas el plan: menú ⋮ → «Participantes e invitar» → «Crear enlace de invitación». Cópialo y pásalo por WhatsApp o como prefieras.',
          'Quien lo abra podrá unirse. Si no tiene cuenta, se la crea en ese momento y entra directamente, sin esperar a ningún administrador.',
          'Cuando estéis todos, puedes cerrar la invitación o cambiar el enlace; el anterior deja de funcionar.',
        ],
      },
      {
        id: 'amedias-quien',
        pregunta: 'Me han invitado: ¿qué es «¿Quién eres en este plan?»?',
        respuesta: [
          'Si ya te habían apuntado por tu nombre, pulsa «Soy …» y te quedas con todos tus gastos. Si no estabas, pulsa «Soy nuevo».',
        ],
      },
      {
        id: 'amedias-roles',
        pregunta: '¿Qué diferencia hay entre organizador y miembro?',
        respuesta: [
          '- Miembro: ve el plan, apunta gastos y pagos, y puede cambiar o borrar los suyos.',
          '- Organizador: además puede tocar los de todos, gestionar participantes, invitar, archivar y borrar el plan.',
          'Quien crea el plan es organizador. Puede nombrar a otros con la corona 👑 en «Participantes». Siempre queda al menos uno.',
        ],
      },
      {
        id: 'amedias-recordatorios',
        pregunta: '¿Cómo funcionan los recordatorios?',
        respuesta: [
          'Cada plan tiene un plazo (por defecto, 7 días) que se cuenta desde la fecha de fin del plan o, si no tiene, desde el último gasto.',
          'Pasado ese plazo, a quien deba le sale un aviso en la app con lo que le queda por pagar. Puede marcar «Ya he pagado» o pedir que se lo recuerde más tarde.',
          'Quien cobra puede mandar un recordatorio cuando quiera con la campanita 🔔 de «Quién debe a quién»: se abre WhatsApp con el mensaje ya escrito.',
          'El plazo se cambia en «Editar plan» (o se quita con «No recordar»).',
        ],
      },
      {
        id: 'amedias-pdf',
        pregunta: '¿Puedo descargar un resumen del plan?',
        respuesta: [
          'Sí: menú ⋮ → «Descargar PDF». Incluye quién debe a quién, el resumen por persona y la lista de gastos y pagos. Va bien para mandarlo al grupo al cerrar cuentas.',
        ],
      },
      {
        id: 'amedias-finanzas',
        pregunta: 'Uso Finanzas: ¿se apuntan mis gastos solos?',
        respuesta: [
          'Si quieres, sí. En el plan, menú ⋮ → «Pasar a Finanzas», y elige la cuenta y la categoría.',
          '- Tu parte de cada gasto entra como gasto en esa categoría.',
          '- Lo que adelantas por los demás entra como adelanto, con el plan como persona: lo verás pendiente en el inicio de Finanzas hasta que saldéis.',
          '- Los pagos del plan cierran ese pendiente.',
          'Lo que apuntas tú pasa solo. Lo que apuntan otros te lo enseñamos antes para que lo confirmes, salvo que marques que pase sin preguntar.',
        ],
      },
      {
        id: 'amedias-salir',
        pregunta: '¿Cómo salgo de un plan, o lo archivo?',
        respuesta: [
          'En «Participantes» tienes «Salir del plan». Dejarás de verlo, pero tus gastos se quedan para que las cuentas sigan cuadrando.',
          'Cuando un plan está saldado, quien lo organiza puede archivarlo: sale de la lista principal sin borrar nada.',
        ],
      },
    ],
  },
  {
    id: 'tareas',
    titulo: 'Tareas: las de casa, repartidas',
    emoji: '🧹',
    intro: 'Apunta las tareas de casa, repártelas entre quienes vivís en ella y vedlas todos en una misma semana.',
    preguntas: [
      {
        id: 'tareas-empezar',
        pregunta: '¿Por dónde empiezo?',
        respuesta: [
          'Entra en Tareas. Tienes ya una casa solo tuya, «Mis tareas», para lo personal.',
          'Pulsa «Elegir las habituales» para crear de golpe las más comunes (fregar, la basura, la lavadora…) o «Nueva tarea» para escribir la tuya: qué es, cada cuánto se repite, qué día toca y, si quieres, a qué hora avisar.',
        ],
        accion: { texto: 'Ir a Tareas', ruta: '/tareas', modulo: 'tareas' },
      },
      {
        id: 'tareas-casa',
        pregunta: '¿Cómo comparto las tareas con quien vive conmigo?',
        respuesta: [
          'En «Casas», pulsa «Nueva casa» y apunta a quien vive en ella. Basta con el nombre: los niños, por ejemplo, no necesitan cuenta.',
          'Después, con el botón de personas, crea un enlace de invitación y pásalo por WhatsApp. Quien lo abra se une a la casa y dice quién es.',
          'Todos veréis la misma semana, cada persona con su color, y podréis crear y marcar tareas. Quien organiza la casa gestiona la gente y el enlace.',
        ],
      },
      {
        id: 'tareas-semana',
        pregunta: '¿Cómo se lee la semana?',
        respuesta: [
          'En el móvil eliges el día en la tira de arriba; en el iPad o el ordenador ves los siete a la vez. El color del borde dice a quién le toca.',
          '- Círculo vacío: toca hacerla. Tócalo cuando la hagas.',
          '- Reloj en gris: la vez siguiente, para que veas lo que viene.',
          '- Tachada: ya hecha, y por quién.',
          'Lo que se quedó sin hacer sale arriba, en «Atrasadas». «Solo las mías» deja solo lo que te toca a ti.',
        ],
      },
      {
        id: 'tareas-marcar',
        pregunta: '¿Y si la hizo otra persona, o hoy no se hace?',
        respuesta: [
          'Toca el nombre de la tarea. Puedes decir quién la ha hecho, saltarla esta vez, dejarla para mañana o moverla a otro día.',
          'Si te equivocas, toca la tarea ya hecha y pulsa «Deshacer».',
          'Al hacerla, pasa sola a la siguiente vez. Si la haces con retraso, no se te acumulan las de los días que pasaron.',
        ],
      },
      {
        id: 'tareas-avisos',
        pregunta: '¿Me avisa el móvil?',
        respuesta: [
          'En la app del iPhone, con la campana de Tareas. Te avisa de las tareas que te tocan a ti: las que tienen hora, a esa hora, y el resto juntas por la mañana.',
        ],
      },
      {
        id: 'tareas-compra',
        pregunta: '¿Hay una lista de la compra?',
        respuesta: [
          'Sí, en Tareas → Compra. Cada casa tiene la suya: cualquiera apunta lo que falta y quien va a comprar lo tacha; los demás lo ven al momento en su móvil. Lo tachado queda «En el carro» hasta que lo vacías.',
          'Debajo del cuadro salen las cosas que apuntas a menudo, para añadirlas de un toque. La lista de la compra es gratis.',
        ],
      },
      {
        id: 'tareas-peques',
        pregunta: '¿Y los niños?',
        respuesta: [
          'No necesitan cuenta: añádelos a la casa solo con su nombre y, en el botón de personas, pulsa la estrella para marcarlos como peques. Cada tarea que hacen les suma estrellas según su esfuerzo (poco 1, medio 2, mucho 3), y en la semana sale cuántas llevan esta semana y este mes. El premio lo ponéis vosotros.',
          'Al marcar una tarea, elige en «¿Quién la ha hecho?» que ha sido el peque.',
        ],
      },
      {
        id: 'tareas-mantenimiento',
        pregunta: '¿Me recuerda lo que toca cada mucho tiempo, como la caldera?',
        respuesta: [
          'Sí. En «Añadir tareas habituales» hay un apartado «Mantenimiento de la casa»: la revisión de la caldera, los filtros del aire acondicionado, descalcificar la cafetera, los detectores de humo… Se reparten en las próximas semanas para que no caigan todas el mismo día y luego cada una vuelve cuando toca (cada 3 meses, cada año…).',
          'Si al hacer una cuesta dinero (la revisión, la bombona, la compra), toca la tarea y pulsa «Hecha y apuntar lo que ha costado»: se marca hecha y se abre el gasto en Finanzas con el nombre ya puesto.',
        ],
      },
      {
        id: 'tareas-turnos',
        pregunta: '¿Se pueden hacer tareas por turnos?',
        respuesta: [
          'Sí. Al crear o editar una tarea que se repite, en «¿A quién le toca?» elige «Por turnos» y toca a las personas en el orden en que les toca. Cada vez que alguien la marca hecha, pasa a la siguiente; si se salta, le sigue tocando a la misma.',
          'Debajo de la semana, «Cómo va el reparto» cuenta quién ha hecho cuánto (teniendo en cuenta el esfuerzo de cada tarea), si va equilibrado y lo que le toca a cada uno los próximos días.',
          `Con las notificaciones activadas en el iPhone, te avisamos cuando te asignan una tarea o te llega el turno, y el domingo por la tarde te mandamos el resumen de la semana. Es de ${NOMBRE_APP} Plus (el tuyo o el de quien organiza tu casa).`,
        ],
      },
      {
        id: 'tareas-calendario',
        pregunta: '¿Puedo ver las tareas en mi calendario o en Recordatorios?',
        respuesta: [
          'Sí, con el botón del calendario en Tareas. «Crear mi enlace de calendario» te da un enlace que se añade una vez al Calendario del iPhone o del Mac, a Google Calendar o a Outlook, y se actualiza solo.',
          'En el iPhone y el iPad puedes además poner en Recordatorios las tareas que te tocan, en una lista «myarinn · Tareas». Si tachas una allí, al abrir myarinn se marca hecha para toda la casa.',
          `Las dos cosas son de ${NOMBRE_APP} Plus (el tuyo o el de quien organiza tu casa).`,
        ],
      },
      {
        id: 'tareas-plus',
        pregunta: '¿Qué tiene de más con Plus?',
        respuesta: [
          `Gratis tienes «Mis tareas» y una casa compartida que organices, con hasta 10 tareas que se repiten en cada casa. Con ${NOMBRE_APP} Plus, todas las casas y tareas que quieras, tareas por turnos, cómo va el reparto, avisos al móvil de lo que te toca y tus tareas en el Calendario y en Recordatorios.`,
          'El Plus de quien organiza una casa vale para todos los que viven en ella.',
        ],
      },
    ],
  },
  {
    id: 'finanzas',
    titulo: 'Finanzas',
    emoji: '💶',
    intro: 'Tus cuentas, lo que entra y sale, presupuestos, movimientos que se repiten, informes e inversiones.',
    preguntas: [
      {
        id: 'finanzas-empezar',
        pregunta: '¿Por dónde empiezo?',
        respuesta: [
          'La primera vez que entras en Finanzas te guía un asistente de cuatro pasos. Lo imprescindible es una cuenta: los gastos e ingresos siempre se apuntan en una, así que sin cuenta no se puede guardar ninguno.',
          '- Te explica cómo funciona: cuentas, gastos e ingresos, y presupuestos.',
          '- Creas tu primera cuenta: la del banco, el efectivo, el ahorro o una tarjeta, con lo que tiene ahora.',
          '- Eliges las categorías básicas (vivienda, supermercado, nómina…). Quita las que no uses.',
          '- Y listo: puedes apuntar tu primer gasto.',
          'Si lo cerraste, el inicio de Finanzas muestra «Empieza creando tu primera cuenta» para retomarlo. Más cuentas y categorías se crean en Configuración, y los movimientos con el botón + o «Agregar transacción».',
          'Puedes volver a abrirlo cuando quieras con el botón de aquí debajo. Si ya tienes cuentas o categorías, se salta esos pasos y te ayuda con lo demás: lo que se repite cada mes, un presupuesto y los recordatorios.',
        ],
        accion: { texto: 'Abrir el asistente', ruta: '/finanzas/dashboard?asistente=1', modulo: 'finanzas' },
        capturas: [
          { archivo: 'asistente-1', texto: '1. Cómo funciona' },
          { archivo: 'asistente-2', texto: '2. Tu primera cuenta' },
          { archivo: 'asistente-3', texto: '3. Tus categorías' },
          { archivo: 'asistente-4', texto: '4. ¡Todo listo!' },
        ],
      },
      {
        id: 'finanzas-cuentas',
        pregunta: '¿Qué tipos de cuenta hay?',
        respuesta: [
          '- Corriente y ahorro: las del banco.',
          '- Tarjeta: si es de crédito, márcala para que pueda quedarse en negativo.',
          '- Hucha: para ahorrar con un objetivo; verás cuánto te falta.',
          'Puedes ocultar el saldo de una cuenta (por ejemplo, la de ahorro) para que no salga a la vista. El saldo se calcula solo a partir del saldo inicial y los movimientos.',
        ],
      },
      {
        id: 'finanzas-transacciones',
        pregunta: '¿Cómo apunto un gasto, un ingreso o un traspaso?',
        respuesta: [
          'Pulsa «Agregar transacción» y elige el tipo:',
          '- Gasto o ingreso: importe, categoría, cuenta, fecha y una descripción si quieres.',
          '- Traspaso: dinero que pasa de una cuenta tuya a otra (o a una inversión). No cuenta como gasto ni ingreso; solo mueve saldo.',
          'Todo se puede editar o borrar después desde Transacciones, donde también puedes filtrar y buscar.',
          'Si escribes la descripción antes de elegir la categoría (por ejemplo «Mercadona»), la app pone la categoría, la cuenta y el presupuesto de la última vez que apuntaste algo ahí. Puedes cambiarlos antes de guardar.',
        ],
      },
      {
        id: 'finanzas-atajos',
        pregunta: '¿Puedo apuntar un gasto más rápido desde el iPhone?',
        respuesta: [
          'Sí. La app de myarinn trae sus propios atajos: salen solos en la app Atajos, en Siri y al buscar en el iPhone, sin crear nada.',
          '- Di «Oye Siri, apunta un gasto en myarinn» (o «…un ingreso…»): te pregunta cuánto y se abre myarinn con el gasto rellenado.',
          '- O busca «Apuntar gasto» en el iPhone, o ponlo en el botón de Acción o en la pantalla de inicio desde la app Atajos.',
          'Solo tienes que revisarlo y pulsar Guardar. Si escribes dónde (por ejemplo «Mercadona»), pone la categoría y la cuenta de la última vez allí. Funciona también sin conexión. Necesita iOS 16 o más nuevo.',
        ],
      },
      {
        id: 'finanzas-apple-pay',
        pregunta: '¿Se puede apuntar solo al pagar con Apple Pay?',
        respuesta: [
          'Sí, con myarinn Plus. Cada vez que pagas con Apple Pay en una tienda se abre myarinn con el importe, la tienda y la categoría de la última vez. Lo revisas y guardas.',
          'Se configura una vez en la app Atajos, porque Apple no deja que ninguna app lo haga sola:',
          '- Atajos → Automatización → + → «Transacción».',
          '- Marca tus tarjetas y elige «Ejecutar inmediatamente».',
          '- Añade la acción «Apuntar pago de Apple Pay» de myarinn y, en Importe, Comercio y Tarjeta, elige las variables de la transacción con ese nombre.',
          'La primera vez que pagas con cada tarjeta, myarinn pregunta en qué cuenta va y ya lo recuerda. Las tarjetas de cada cuenta se ven y se quitan al editarla en Configuración → Cuentas.',
          'Solo funciona con Apple Pay en tiendas: no con la tarjeta física ni con la mayoría de compras por internet.',
        ],
        accion: { texto: 'Ver los pasos', ruta: '/finanzas/dashboard?applepay=1', modulo: 'finanzas' },
      },
      {
        id: 'finanzas-adelantos',
        pregunta: '¿Qué es un adelanto?',
        respuesta: [
          'Dinero que pagas por otra persona y que te va a devolver (o que te adelantan a ti). Al apuntar el gasto, marca «Adelanto» e indica la persona. Cuando te lo devuelva, apunta un ingreso marcado como «Devolución de adelanto».',
          'Mueve el saldo de tu cuenta, pero no cuenta como gasto ni como ingreso, así que no ensucia tus informes ni tus presupuestos.',
          'En el inicio de Finanzas verás cuánto te debe cada persona. Si queda un resto de céntimos, «Saldar» cierra las cuentas con esa persona.',
        ],
      },
      {
        id: 'finanzas-presupuestos',
        pregunta: '¿Cómo funcionan los presupuestos?',
        respuesta: [
          'Un presupuesto es un límite de gasto para una categoría, por mes o por año. La app suma lo gastado y te avisa si te pasas.',
          'Si lo marcas como repetitivo, vale todos los meses desde el de inicio. Al apuntar un gasto puedes elegir a qué presupuesto va, útil si tienes varios de la misma categoría.',
          '¿Un mes tienes menos dinero? En Presupuestos ve a ese mes, pulsa el lápiz del presupuesto y cambia el importe. Te preguntará para qué meses es:',
          '- Solo ese mes: los demás siguen igual.',
          '- Desde ese mes en adelante: los meses anteriores conservan el importe que tenían, así tus informes pasados no cambian.',
          'Sigue siendo el mismo presupuesto: los gastos que ya tenía apuntados no se tocan ni se duplican.',
          'Desde Presupuestos puedes descargar un PDF con el resumen.',
        ],
      },
      {
        id: 'finanzas-periodicos',
        pregunta: '¿Qué son los periódicos?',
        respuesta: [
          'Movimientos que se repiten: el alquiler, la nómina, un traspaso a la hucha cada mes… Los configuras una vez con su periodicidad (diaria, semanal, mensual, anual…).',
          'Se apuntan solos cada mañana cuando toca. Si alguno se quedó pendiente, sale resaltado y puedes ejecutarlo a mano con ▶.',
        ],
      },
      {
        id: 'finanzas-informes',
        pregunta: '¿Qué me enseñan los informes?',
        respuesta: [
          'Arriba eliges el mes o el año y la sección. Ni los traspasos ni los adelantos cuentan como gasto o ingreso: solo lo que de verdad gastas o ingresas.',
          '- Resumen: ingresos, gastos, ahorro y tasa de ahorro (lo que te queda de cada euro que entra), y una gráfica de los últimos 12 meses.',
          '- Categorías: tus gastos o ingresos por categoría comparados con el mes anterior. Toca una para ver su evolución y sus presupuestos. En el mes en curso, el ritmo se mide contra su presupuesto si tiene.',
          '- Presupuestos: cuáles van bien, cuáles van justos y cuáles te has pasado, y cómo acabarás el mes al ritmo de ahora.',
          '- Cuentas: en qué cuenta o tarjeta se va el dinero y cuándo llenarás cada hucha.',
          '- Hábitos: tus gastos más grandes y dónde repites (para eso, escribe el sitio siempre igual).',
          'Con Plus, además: patrimonio, comparar con el mismo mes del año pasado o con tu media de 6 meses, gastos fijos y variables, qué días gastas más, adelantos por persona y A medias, el resumen de cada mes (con aviso el día 1 si lo activas en Perfil) y el PDF completo.',
        ],
      },
      {
        id: 'finanzas-inversiones',
        pregunta: '¿Cómo llevo mis inversiones?',
        respuesta: [
          'En Inversiones das de alta cada producto (fondo, ETF, depósito, plan de pensiones…). Las aportaciones se hacen con un traspaso desde una cuenta, y se descuenta la comisión si la tiene.',
          'Actualiza el valor de vez en cuando y verás cuánto ganas o pierdes. Los retiros devuelven el dinero a una cuenta.',
        ],
      },
      {
        id: 'finanzas-csv',
        pregunta: '¿Puedo importar o exportar mis movimientos?',
        respuesta: [
          'Sí. En Transacciones tienes «CSV» para descargarlos (se abre en Excel) e «Importar CSV» para cargarlos. En Configuración → Datos puedes exportar todo en un único archivo.',
        ],
      },
    ],
  },
  {
    id: 'prestamos',
    titulo: 'Préstamos',
    emoji: '📦',
    intro: 'Para no olvidar las cosas que dejas (o que te dejan): el taladro, un libro, la tienda de campaña…',
    preguntas: [
      {
        id: 'prestamos-anotar',
        pregunta: '¿Cómo anoto un préstamo?',
        respuesta: [
          'Pulsa «Nuevo préstamo», elige si lo dejas tú o te lo dejan, y escribe qué es, quién lo tiene y desde cuándo.',
          'Si quedasteis en una fecha de vuelta, ponla: cuando se pase, el préstamo saldrá arriba y en rojo.',
        ],
      },
      {
        id: 'prestamos-avisos',
        pregunta: '¿Me avisa si algo no ha vuelto a tiempo?',
        respuesta: [
          'Sí, si le pusiste fecha de vuelta. Al día siguiente de esa fecha, al entrar en la app verás arriba en el Inicio quién lo tiene y desde cuándo, con «Ya ha vuelto» para cerrarlo o «Más tarde» para que no salga en una semana. También te avisa de lo que te han dejado a ti y tenías que devolver.',
          'En el iPhone, con myarinn Plus y las notificaciones activadas (Configuración → Perfil), además te llega una notificación por la tarde: el día que vence y, si sigue fuera, una vez por semana. Si tienes varios, van juntos en una sola.',
        ],
      },
      {
        id: 'prestamos-devolver',
        pregunta: 'Ya me lo han devuelto, ¿qué hago?',
        respuesta: [
          'Pulsa «Devuelto». No se borra: queda en el historial con la fecha en que volvió. Si te equivocas, puedes deshacerlo.',
        ],
      },
      {
        id: 'prestamos-persona',
        pregunta: '¿Cómo veo qué tiene cada persona?',
        respuesta: [
          'Arriba salen las personas que tienen algo tuyo (o te han dejado algo). Pulsa una para ver solo lo suyo. También puedes buscar por objeto, persona o nota.',
        ],
      },
      {
        id: 'prestamos-dinero',
        pregunta: '¿Y si lo que presto es dinero?',
        respuesta: [
          'Para el dinero están los adelantos de Finanzas (o un plan de A medias si es entre varios). Préstamos es solo para objetos.',
        ],
      },
    ],
  },
  {
    id: 'taller3d',
    titulo: 'Taller 3D',
    emoji: '🖨️',
    soloConModulo: 'taller3d',
    intro: 'Calcula lo que te cuesta de verdad una impresión 3D y a cuánto venderla.',
    preguntas: [
      {
        id: 'taller3d-calcular',
        pregunta: '¿Cómo calculo el precio de un encargo?',
        respuesta: [
          'En la Calculadora, cada encargo es un pedido con una o varias líneas. Cada línea es un artículo con sus datos de impresión (filamento usado, tiempo, piezas por plato) y su material.',
          'El coste incluye filamento con merma, electricidad, amortización de la impresora, mantenimiento, los fallos y los costes fijos del pedido (diseño, preparación, envío) repartidos entre las líneas.',
          'El precio de venta sale de un multiplicador o fijando un precio objetivo, con IVA y redondeo.',
        ],
      },
      {
        id: 'taller3d-perfiles',
        pregunta: '¿Tengo que meter los datos de la impresora cada vez?',
        respuesta: [
          'No: guarda perfiles de filamento y de impresora y elígelos en cada cálculo.',
        ],
      },
      {
        id: 'taller3d-presupuestos',
        pregunta: '¿Puedo guardar los presupuestos?',
        respuesta: [
          'Sí, con su estado (borrador, enviado, aceptado o rechazado) y, si usas Clientes, asignados a un cliente.',
        ],
      },
    ],
  },
  {
    id: 'clientes',
    titulo: 'Clientes',
    emoji: '👥',
    soloConModulo: 'clientes',
    preguntas: [
      {
        id: 'clientes-ficha',
        pregunta: '¿Qué guardo de cada cliente?',
        respuesta: [
          'Datos de contacto y fiscales (NIF, dirección), notas y su historial de presupuestos: lo aceptado y lo pendiente de respuesta.',
          'Un cliente con presupuestos no se borra: márcalo como inactivo y dejará de salir en los desplegables sin perder su historial.',
        ],
      },
    ],
  },
];
